import { useState, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Phone,
  Upload,
  Mic,
  Plus,
  Clock,
  AlertCircle,
  CheckCircle2,
  Loader2,
  FileAudio,
  Trash2,
  Calendar,
  ChevronRight,
  Mail,
  MessageSquare,
  Database,
  Zap,
  Eye,
  Bot,
  Activity,
  RefreshCw,
  Server,
  Search,
  X,
  Users,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

import { CallUploader } from '@/components/insights/CallUploader';
import { BrowserRecorder } from '@/components/insights/BrowserRecorder';
import { SyncSettingsModal } from '@/components/insights/SyncSettingsModal';
import { useCallProcessor } from '@/hooks/useCallProcessor';
import {
  getCalls,
  getCall,
  createCall,
  uploadAudio,
  deleteCall as deleteCallApi,
  formatDuration,
  getEmails,
  getEmail,
  getChatter,
  getChatterInsight,
  fetchEmailsFromOdoo,
  fetchChatterFromOdoo,
  analyzeAllEmails,
  analyzeAllChatter,
  retryFailedEmails,
  retryFailedChatter,
  retrySingleEmail,
  retrySingleChatter,
  getFailedCounts,
  type CallStatus,
  type CallWithDetails,
  type EmailInsightWithAnalysis,
  type ChatterInsightWithAnalysis,
} from '@/api/insights';
import { getBranches } from '@/api/deployments';

type InsightSource = 'calls' | 'emails' | 'chatter';

interface Deployment {
  buildId: number;
  branchName: string;
  branchType: 'production' | 'staging' | 'development';
  fqdn: string | null;
}

interface ActivityLogEntry {
  id: string;
  timestamp: Date;
  type: 'info' | 'processing' | 'success' | 'error' | 'ai';
  source: InsightSource;
  message: string;
  details?: string;
  recordId?: string;
}

export function InsightsDashboard() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  const [isAddCallOpen, setIsAddCallOpen] = useState(false);
  const [addCallMode, setAddCallMode] = useState<'upload' | 'record'>('upload');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedBuildId, setSelectedBuildId] = useState<number | null>(null);
  const [processingCallId, setProcessingCallId] = useState<string | null>(null);
  const [activeSource, setActiveSource] = useState<InsightSource>('calls');
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailInsightWithAnalysis | null>(null);
  const [isEmailDetailOpen, setIsEmailDetailOpen] = useState(false);
  const [isLoadingEmailDetail, setIsLoadingEmailDetail] = useState(false);
  const [selectedChatter, setSelectedChatter] = useState<ChatterInsightWithAnalysis | null>(null);
  const [isChatterDetailOpen, setIsChatterDetailOpen] = useState(false);
  const [isLoadingChatterDetail, setIsLoadingChatterDetail] = useState(false);
  const [selectedCall, setSelectedCall] = useState<CallWithDetails | null>(null);
  const [isCallDetailOpen, setIsCallDetailOpen] = useState(false);
  const [isLoadingCallDetail, setIsLoadingCallDetail] = useState(false);
  const [emailSearch, setEmailSearch] = useState('');
  const [chatterSearch, setChatterSearch] = useState('');
  const [callSearch, setCallSearch] = useState('');
  const [emailStatusFilter, setEmailStatusFilter] = useState<'all' | 'pending' | 'analyzed'>('all');
  const [chatterStatusFilter, setChatterStatusFilter] = useState<'all' | 'pending' | 'analyzed'>('all');
  const [callStatusFilter, setCallStatusFilter] = useState<'all' | 'pending' | 'analyzed'>('all');
  const [isSyncSettingsOpen, setIsSyncSettingsOpen] = useState(false);

  // Add activity log entry
  const addLogEntry = useCallback((entry: Omit<ActivityLogEntry, 'id' | 'timestamp'>) => {
    setActivityLog(prev => [{
      ...entry,
      id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    }, ...prev].slice(0, 50)); // Keep last 50 entries
  }, []);

  // Fetch branches/deployments
  const { data: branchesData } = useQuery({
    queryKey: ['branches', projectId],
    queryFn: () => getBranches(projectId!),
    enabled: !!projectId,
  });

  // Build deployments list
  const deployments: Deployment[] = (branchesData?.branches_with_builds || [])
    .filter(b => b.latest_build?.id && (b.branch_type === 'production' || b.branch_type === 'staging'))
    .map(b => ({
      buildId: b.latest_build!.id,
      branchName: b.name,
      branchType: b.branch_type,
      fqdn: b.latest_build!.fqdn,
    }));

  // Auto-select first deployment
  useEffect(() => {
    if (deployments.length > 0 && !selectedBuildId) {
      const production = deployments.find(d => d.branchType === 'production');
      setSelectedBuildId(production?.buildId || deployments[0].buildId);
    }
  }, [deployments, selectedBuildId]);

  const selectedDeployment = deployments.find(d => d.buildId === selectedBuildId);

  // Handle deployment change
  const handleDeploymentChange = (value: string) => {
    const newBuildId = parseInt(value, 10);
    setSelectedBuildId(newBuildId);
    const dep = deployments.find(d => d.buildId === newBuildId);
    addLogEntry({
      type: 'info',
      source: 'calls',
      message: `Switched to ${dep?.branchName || 'unknown'} deployment`,
      details: dep?.fqdn || undefined,
    });
  };

  // Fetch calls
  const { data: callsData, isLoading: isLoadingCalls, refetch: refetchCalls } = useQuery({
    queryKey: ['calls', projectId],
    queryFn: () => getCalls(projectId!, 50, 0),
    enabled: !!projectId,
    refetchInterval: processingCallId ? 5000 : false,
  });

  // Call processor hook with enhanced logging
  const callProcessor = useCallProcessor({
    projectId: projectId!,
    buildId: selectedBuildId || 0,
    onComplete: (result) => {
      toast.success('Call processing complete!');
      setProcessingCallId(null);
      queryClient.invalidateQueries({ queryKey: ['calls', projectId] });

      addLogEntry({
        type: 'success',
        source: 'calls',
        message: 'Analysis complete',
        details: result.analysis?.summary?.slice(0, 100) || 'Transcript and analysis ready',
        recordId: processingCallId || undefined,
      });
    },
    onError: (error) => {
      toast.error(`Processing failed: ${error}`);
      addLogEntry({
        type: 'error',
        source: 'calls',
        message: 'Processing failed',
        details: error,
        recordId: processingCallId || undefined,
      });
      setProcessingCallId(null);
    },
  });

  // Log processing progress
  useEffect(() => {
    if (callProcessor.progress && processingCallId) {
      const phaseMessages: Record<string, string> = {
        uploading: 'Uploading audio to R2 storage...',
        transcribing: 'Running Whisper transcription (@cf/openai/whisper)...',
        analyzing: 'Analyzing with LLaMA 3.3 70B (@cf/meta/llama-3.3-70b-instruct-fp8-fast)...',
        scoring: 'Generating MEDDIC scorecard...',
        complete: 'Processing complete!',
      };

      if (callProcessor.progress.phase) {
        addLogEntry({
          type: callProcessor.progress.phase === 'complete' ? 'success' : 'ai',
          source: 'calls',
          message: phaseMessages[callProcessor.progress.phase] || callProcessor.progress.message,
          details: `${callProcessor.progress.progress}% complete`,
          recordId: processingCallId,
        });
      }
    }
  }, [callProcessor.progress?.phase, processingCallId, addLogEntry]);

  // Create call mutation
  const createCallMutation = useMutation({
    mutationFn: (input: { source: 'upload' | 'browser'; buildId: number }) =>
      createCall(projectId!, {
        source: input.source,
        buildId: input.buildId,
      }),
    onSuccess: (call) => {
      queryClient.invalidateQueries({ queryKey: ['calls', projectId] });
      addLogEntry({
        type: 'info',
        source: 'calls',
        message: `Created call record`,
        details: `ID: ${call.id}`,
        recordId: call.id,
      });
    },
  });

  // Fetch emails query
  const { data: emailsData, isLoading: isLoadingEmails, refetch: refetchEmails } = useQuery({
    queryKey: ['emails', projectId],
    queryFn: () => getEmails(projectId!, 50, 0),
    enabled: !!projectId && activeSource === 'emails',
  });

  // Fetch chatter query
  const { data: chatterData, isLoading: isLoadingChatter, refetch: refetchChatter } = useQuery({
    queryKey: ['chatter', projectId],
    queryFn: () => getChatter(projectId!, 50, 0),
    enabled: !!projectId && activeSource === 'chatter',
  });

  // Filtered calls based on search and status filter
  const filteredCalls = callsData?.calls?.filter(call => {
    // Status filter
    if (callStatusFilter !== 'all' && call.status !== callStatusFilter) {
      return false;
    }
    // Search filter
    if (callSearch.trim()) {
      const searchLower = callSearch.toLowerCase();
      return (
        (call.title?.toLowerCase().includes(searchLower)) ||
        (call.id.toLowerCase().includes(searchLower)) ||
        (call.source?.toLowerCase().includes(searchLower))
      );
    }
    return true;
  }) || [];

  // Filtered emails based on search and status filter
  const filteredEmails = emailsData?.emails?.filter(email => {
    // Status filter
    if (emailStatusFilter !== 'all' && email.status !== emailStatusFilter) {
      return false;
    }
    // Search filter
    if (emailSearch.trim()) {
      const searchLower = emailSearch.toLowerCase();
      return (
        (email.subject?.toLowerCase().includes(searchLower)) ||
        (email.body_text?.toLowerCase().includes(searchLower)) ||
        (email.author_name?.toLowerCase().includes(searchLower))
      );
    }
    return true;
  }) || [];

  // Filtered chatter based on search and status filter
  const filteredChatter = chatterData?.chatter?.filter(note => {
    // Status filter
    if (chatterStatusFilter !== 'all' && note.status !== chatterStatusFilter) {
      return false;
    }
    // Search filter
    if (chatterSearch.trim()) {
      const searchLower = chatterSearch.toLowerCase();
      return (
        (note.body_text?.toLowerCase().includes(searchLower)) ||
        (note.author_name?.toLowerCase().includes(searchLower)) ||
        (note.odoo_model?.toLowerCase().includes(searchLower))
      );
    }
    return true;
  }) || [];

  // Fetch emails from Odoo mutation
  const fetchEmailsMutation = useMutation({
    mutationFn: () => {
      if (!selectedBuildId) throw new Error('No deployment selected');
      return fetchEmailsFromOdoo(projectId!, { buildId: selectedBuildId, limit: 100 });
    },
    onMutate: () => {
      addLogEntry({
        type: 'processing',
        source: 'emails',
        message: 'Fetching emails from Odoo...',
        details: `Build ID: ${selectedBuildId}`,
      });
    },
    onSuccess: (result) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['emails', projectId] });
      addLogEntry({
        type: 'success',
        source: 'emails',
        message: `Imported ${result.count} emails`,
        details: 'Ready for analysis',
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to fetch emails');
      addLogEntry({
        type: 'error',
        source: 'emails',
        message: 'Failed to fetch emails',
        details: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Fetch chatter from Odoo mutation
  const fetchChatterMutation = useMutation({
    mutationFn: () => {
      if (!selectedBuildId) throw new Error('No deployment selected');
      return fetchChatterFromOdoo(projectId!, { buildId: selectedBuildId, limit: 100 });
    },
    onMutate: () => {
      addLogEntry({
        type: 'processing',
        source: 'chatter',
        message: 'Fetching chatter from Odoo...',
        details: `Build ID: ${selectedBuildId}`,
      });
    },
    onSuccess: (result) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['chatter', projectId] });
      addLogEntry({
        type: 'success',
        source: 'chatter',
        message: `Imported ${result.count} notes`,
        details: 'Ready for analysis',
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to fetch chatter');
      addLogEntry({
        type: 'error',
        source: 'chatter',
        message: 'Failed to fetch chatter',
        details: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Analyze all emails mutation (loops until all are processed)
  const analyzeEmailsMutation = useMutation({
    mutationFn: async () => {
      let totalAnalyzed = 0;
      let totalFailed = 0;
      let remaining = 1; // Start with 1 to enter loop
      let batchCount = 0;

      while (remaining > 0) {
        batchCount++;
        const result = await analyzeAllEmails(projectId!);
        totalAnalyzed += result.analyzed;
        totalFailed += result.failed;
        remaining = result.remaining;

        // Update progress in real-time
        if (remaining > 0) {
          addLogEntry({
            type: 'ai',
            source: 'emails',
            message: `Batch ${batchCount}: Analyzed ${result.analyzed} emails`,
            details: `${remaining} remaining...`,
          });
          // Refresh the list to show progress
          queryClient.invalidateQueries({ queryKey: ['emails', projectId] });
        }
      }

      return { analyzed: totalAnalyzed, failed: totalFailed, remaining: 0, message: `Analyzed ${totalAnalyzed} emails` };
    },
    onMutate: () => {
      addLogEntry({
        type: 'ai',
        source: 'emails',
        message: 'Starting AI analysis...',
        details: 'Using LLaMA 3.3 70B for email analysis (processing in batches)',
      });
    },
    onSuccess: (result) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['emails', projectId] });
      addLogEntry({
        type: 'success',
        source: 'emails',
        message: `Analyzed ${result.analyzed} emails`,
        details: result.failed > 0 ? `${result.failed} failed` : 'All emails analyzed successfully',
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Analysis failed');
      addLogEntry({
        type: 'error',
        source: 'emails',
        message: 'AI analysis failed',
        details: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Analyze all chatter mutation (loops until all are processed)
  const analyzeChatterMutation = useMutation({
    mutationFn: async () => {
      let totalAnalyzed = 0;
      let totalFailed = 0;
      let remaining = 1; // Start with 1 to enter loop
      let batchCount = 0;

      while (remaining > 0) {
        batchCount++;
        const result = await analyzeAllChatter(projectId!);
        totalAnalyzed += result.analyzed;
        totalFailed += result.failed;
        remaining = result.remaining;

        // Update progress in real-time
        if (remaining > 0) {
          addLogEntry({
            type: 'ai',
            source: 'chatter',
            message: `Batch ${batchCount}: Analyzed ${result.analyzed} messages`,
            details: `${remaining} remaining...`,
          });
          // Refresh the list to show progress
          queryClient.invalidateQueries({ queryKey: ['chatter', projectId] });
        }
      }

      return { analyzed: totalAnalyzed, failed: totalFailed, remaining: 0, message: `Analyzed ${totalAnalyzed} chatter messages` };
    },
    onMutate: () => {
      addLogEntry({
        type: 'ai',
        source: 'chatter',
        message: 'Starting AI analysis...',
        details: 'Using LLaMA 3.3 70B for chatter analysis (processing in batches)',
      });
    },
    onSuccess: (result) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['chatter', projectId] });
      addLogEntry({
        type: 'success',
        source: 'chatter',
        message: `Analyzed ${result.analyzed} chatter messages`,
        details: result.failed > 0 ? `${result.failed} failed` : 'All chatter analyzed successfully',
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Analysis failed');
      addLogEntry({
        type: 'error',
        source: 'chatter',
        message: 'AI analysis failed',
        details: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Delete call mutation
  const deleteCallMutation = useMutation({
    mutationFn: (callId: string) => deleteCallApi(callId),
    onSuccess: (_, callId) => {
      toast.success('Call deleted');
      queryClient.invalidateQueries({ queryKey: ['calls', projectId] });
      addLogEntry({
        type: 'info',
        source: 'calls',
        message: 'Deleted call record',
        recordId: callId,
      });
    },
    onError: () => {
      toast.error('Failed to delete call');
    },
  });

  // Query for failed counts
  const { data: failedCounts } = useQuery({
    queryKey: ['failed-counts', projectId],
    queryFn: () => getFailedCounts(projectId!),
    enabled: !!projectId,
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Retry failed emails mutation
  const retryEmailsMutation = useMutation({
    mutationFn: () => retryFailedEmails(projectId!),
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['emails', projectId] });
      queryClient.invalidateQueries({ queryKey: ['failed-counts', projectId] });
      addLogEntry({
        type: 'success',
        source: 'emails',
        message: `Reset ${data.reset || 0} failed emails to pending`,
      });
    },
    onError: (error) => {
      toast.error('Failed to retry emails');
      addLogEntry({
        type: 'error',
        source: 'emails',
        message: 'Failed to retry emails',
        details: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Retry failed chatter mutation
  const retryChatterMutation = useMutation({
    mutationFn: () => retryFailedChatter(projectId!),
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['chatter', projectId] });
      queryClient.invalidateQueries({ queryKey: ['failed-counts', projectId] });
      addLogEntry({
        type: 'success',
        source: 'chatter',
        message: `Reset ${data.reset || 0} failed chatter messages to pending`,
      });
    },
    onError: (error) => {
      toast.error('Failed to retry chatter');
      addLogEntry({
        type: 'error',
        source: 'chatter',
        message: 'Failed to retry chatter',
        details: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Retry single email mutation
  const retrySingleEmailMutation = useMutation({
    mutationFn: (emailId: string) => retrySingleEmail(emailId),
    onSuccess: (data, emailId) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['emails', projectId] });
      queryClient.invalidateQueries({ queryKey: ['failed-counts', projectId] });
      setIsEmailDetailOpen(false);
      setSelectedEmail(null);
      addLogEntry({
        type: 'success',
        source: 'emails',
        message: 'Reset email to pending for retry',
        recordId: emailId,
      });
    },
    onError: (error) => {
      toast.error('Failed to retry email');
      addLogEntry({
        type: 'error',
        source: 'emails',
        message: 'Failed to retry email',
        details: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Retry single chatter mutation
  const retrySingleChatterMutation = useMutation({
    mutationFn: (chatterId: string) => retrySingleChatter(chatterId),
    onSuccess: (data, chatterId) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['chatter', projectId] });
      queryClient.invalidateQueries({ queryKey: ['failed-counts', projectId] });
      setIsChatterDetailOpen(false);
      setSelectedChatter(null);
      addLogEntry({
        type: 'success',
        source: 'chatter',
        message: 'Reset chatter to pending for retry',
        recordId: chatterId,
      });
    },
    onError: (error) => {
      toast.error('Failed to retry chatter');
      addLogEntry({
        type: 'error',
        source: 'chatter',
        message: 'Failed to retry chatter',
        details: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Handle email click - load analysis and show detail dialog
  const handleEmailClick = useCallback(async (emailId: string) => {
    setIsLoadingEmailDetail(true);
    setIsEmailDetailOpen(true);
    try {
      const emailWithAnalysis = await getEmail(emailId, true) as EmailInsightWithAnalysis;
      setSelectedEmail(emailWithAnalysis);
    } catch (error) {
      toast.error('Failed to load email details');
      setIsEmailDetailOpen(false);
    } finally {
      setIsLoadingEmailDetail(false);
    }
  }, []);

  // Handle chatter click - load analysis and show detail dialog
  const handleChatterClick = useCallback(async (chatterId: string) => {
    setIsLoadingChatterDetail(true);
    setIsChatterDetailOpen(true);
    try {
      const chatterWithAnalysis = await getChatterInsight(chatterId, true) as ChatterInsightWithAnalysis;
      setSelectedChatter(chatterWithAnalysis);
    } catch (error) {
      toast.error('Failed to load chatter details');
      setIsChatterDetailOpen(false);
    } finally {
      setIsLoadingChatterDetail(false);
    }
  }, []);

  // Handle call click - load details and show dialog
  const handleCallClick = useCallback(async (callId: string) => {
    setIsLoadingCallDetail(true);
    setIsCallDetailOpen(true);
    try {
      const callWithDetails = await getCall(callId, true) as CallWithDetails;
      setSelectedCall(callWithDetails);
    } catch (error) {
      toast.error('Failed to load call details');
      setIsCallDetailOpen(false);
    } finally {
      setIsLoadingCallDetail(false);
    }
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback(async (file: File) => {
    if (!selectedBuildId) {
      toast.error('No deployment selected');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(10);

      addLogEntry({
        type: 'processing',
        source: 'calls',
        message: `Uploading ${file.name}`,
        details: `Size: ${(file.size / 1024 / 1024).toFixed(2)} MB`,
      });

      const call = await createCallMutation.mutateAsync({
        source: 'upload',
        buildId: selectedBuildId,
      });

      setUploadProgress(30);

      await uploadAudio(call.id, file);

      setUploadProgress(100);

      addLogEntry({
        type: 'success',
        source: 'calls',
        message: 'Audio uploaded to R2',
        recordId: call.id,
      });

      await callProcessor.connect();
      setProcessingCallId(call.id);

      setTimeout(() => {
        callProcessor.processCall(call.id);
      }, 500);

      setIsAddCallOpen(false);
      toast.success('Upload complete! Processing started.');
    } catch (error) {
      addLogEntry({
        type: 'error',
        source: 'calls',
        message: 'Upload failed',
        details: error instanceof Error ? error.message : 'Unknown error',
      });
      toast.error(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [selectedBuildId, createCallMutation, callProcessor, addLogEntry]);

  // Handle recording upload
  const handleRecordingComplete = useCallback(async (blob: Blob) => {
    if (!selectedBuildId) {
      toast.error('No deployment selected');
      return;
    }

    try {
      setIsUploading(true);

      addLogEntry({
        type: 'processing',
        source: 'calls',
        message: 'Processing browser recording',
        details: `Size: ${(blob.size / 1024 / 1024).toFixed(2)} MB`,
      });

      const call = await createCallMutation.mutateAsync({
        source: 'browser',
        buildId: selectedBuildId,
      });

      await uploadAudio(call.id, blob);

      await callProcessor.connect();
      setProcessingCallId(call.id);

      setTimeout(() => {
        callProcessor.processCall(call.id);
      }, 500);

      setIsAddCallOpen(false);
      toast.success('Recording uploaded! Processing started.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [selectedBuildId, createCallMutation, callProcessor, addLogEntry]);

  const getStatusBadge = (status: CallStatus) => {
    const colors: Record<CallStatus, string> = {
      pending: 'bg-gray-100 text-gray-700',
      processing: 'bg-blue-100 text-blue-700',
      transcribed: 'bg-yellow-100 text-yellow-700',
      analyzed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
    };

    const labels: Record<CallStatus, string> = {
      pending: 'Pending',
      processing: 'Processing',
      transcribed: 'Transcribed',
      analyzed: 'Analyzed',
      failed: 'Failed',
    };

    return (
      <Badge className={cn('font-medium', colors[status])}>
        {status === 'processing' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
        {status === 'analyzed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
        {status === 'failed' && <AlertCircle className="h-3 w-3 mr-1" />}
        {labels[status]}
      </Badge>
    );
  };

  const getLogIcon = (type: ActivityLogEntry['type']) => {
    switch (type) {
      case 'info': return <Database className="h-3 w-3 text-gray-400" />;
      case 'processing': return <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />;
      case 'success': return <CheckCircle2 className="h-3 w-3 text-green-500" />;
      case 'error': return <AlertCircle className="h-3 w-3 text-red-500" />;
      case 'ai': return <Bot className="h-3 w-3 text-purple-500" />;
    }
  };

  const stats = {
    totalCalls: callsData?.total || 0,
    analyzedCalls: callsData?.calls.filter(c => c.status === 'analyzed').length || 0,
    processingCalls: callsData?.calls.filter(c => c.status === 'processing').length || 0,
    totalDuration: callsData?.calls.reduce((acc, c) => acc + (c.duration_seconds || 0), 0) || 0,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <Zap className="h-6 w-6 mr-2 text-blue-500" />
              Conversation Intelligence
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Analyze calls, emails, and chatter to unlock insights
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Customer Insights Link */}
            <Button variant="outline" asChild>
              <Link to={`/project/${projectId}/insights/customers`}>
                <Users className="h-4 w-4 mr-2" />
                Customer Insights
              </Link>
            </Button>

            {/* Sync Settings Button */}
            <Button variant="outline" onClick={() => setIsSyncSettingsOpen(true)}>
              <Database className="h-4 w-4 mr-2" />
              Sync Settings
            </Button>

            {/* Deployment Selector */}
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4 text-gray-400" />
              <Select
                value={selectedBuildId ? String(selectedBuildId) : ''}
                onValueChange={handleDeploymentChange}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select deployment" />
                </SelectTrigger>
                <SelectContent>
                  {deployments.map((deployment) => (
                    <SelectItem key={deployment.buildId} value={String(deployment.buildId)}>
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "w-2 h-2 rounded-full",
                          deployment.branchType === 'production' && "bg-green-500",
                          deployment.branchType === 'staging' && "bg-blue-500"
                        )} />
                        <span className="truncate">{deployment.branchName}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Dialog open={isAddCallOpen} onOpenChange={setIsAddCallOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Insight
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add New Insight</DialogTitle>
                  <DialogDescription>
                    Upload a call recording, record directly, or fetch from Odoo
                  </DialogDescription>
                </DialogHeader>

                <Tabs value={addCallMode} onValueChange={(v) => setAddCallMode(v as 'upload' | 'record')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="upload">
                      <Upload className="h-4 w-4 mr-2" />
                      Upload File
                    </TabsTrigger>
                    <TabsTrigger value="record">
                      <Mic className="h-4 w-4 mr-2" />
                      Record
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="upload" className="mt-4">
                    <CallUploader
                      onUpload={handleFileUpload}
                      isUploading={isUploading}
                      uploadProgress={uploadProgress}
                      disabled={!selectedBuildId}
                    />
                  </TabsContent>

                  <TabsContent value="record" className="mt-4">
                    <BrowserRecorder
                      onRecordingComplete={handleRecordingComplete}
                      isUploading={isUploading}
                      disabled={!selectedBuildId}
                    />
                  </TabsContent>
                </Tabs>

                {!selectedBuildId && (
                  <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mt-4">
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      Select a deployment above to enable processing.
                    </p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Connection Status */}
        {selectedDeployment && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className={cn(
              "w-2 h-2 rounded-full",
              callProcessor.isConnected ? "bg-green-500" : "bg-gray-400"
            )} />
            <span>
              Connected to <strong>{selectedDeployment.branchName}</strong>
              {selectedDeployment.fqdn && (
                <span className="text-gray-400 ml-1">({selectedDeployment.fqdn})</span>
              )}
            </span>
          </div>
        )}

        {/* Source Tabs */}
        <Tabs value={activeSource} onValueChange={(v) => setActiveSource(v as InsightSource)}>
          <TabsList>
            <TabsTrigger value="calls" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Calls
              <Badge variant="secondary" className="ml-1">{stats.totalCalls}</Badge>
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Emails
              <Badge variant="secondary" className="ml-1">0</Badge>
            </TabsTrigger>
            <TabsTrigger value="chatter" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Chatter
              <Badge variant="secondary" className="ml-1">0</Badge>
            </TabsTrigger>
          </TabsList>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-6">
              {/* Processing Progress */}
              {callProcessor.isProcessing && callProcessor.progress && (
                <Card className="border-blue-200 dark:border-blue-800 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
                  <CardContent className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <Bot className="h-8 w-8 text-purple-500" />
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500 absolute -bottom-1 -right-1" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {callProcessor.progress.message}
                          </p>
                          <span className="text-sm font-mono text-blue-600 dark:text-blue-400">
                            {callProcessor.progress.progress}%
                          </span>
                        </div>
                        <Progress value={callProcessor.progress.progress} className="h-2" />
                        <p className="text-xs text-gray-500 mt-2">
                          {callProcessor.progress.phase === 'transcribing' && 'Using Cloudflare Whisper for speech-to-text...'}
                          {callProcessor.progress.phase === 'analyzing' && 'Running LLaMA 3.3 70B for semantic analysis...'}
                          {callProcessor.progress.phase === 'scoring' && 'Generating MEDDIC framework scores...'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Calls Tab Content */}
              <TabsContent value="calls" className="mt-0">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Total</p>
                          <p className="text-xl font-bold">{stats.totalCalls}</p>
                        </div>
                        <FileAudio className="h-6 w-6 text-gray-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Analyzed</p>
                          <p className="text-xl font-bold text-green-600">{stats.analyzedCalls}</p>
                        </div>
                        <CheckCircle2 className="h-6 w-6 text-green-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Processing</p>
                          <p className="text-xl font-bold text-blue-600">{stats.processingCalls}</p>
                        </div>
                        <Loader2 className={cn("h-6 w-6 text-blue-400", stats.processingCalls > 0 && "animate-spin")} />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Duration</p>
                          <p className="text-xl font-bold">{formatDuration(stats.totalDuration)}</p>
                        </div>
                        <Clock className="h-6 w-6 text-gray-400" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Calls List */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Recent Calls</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => refetchCalls()}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingCalls ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                      </div>
                    ) : callsData?.calls.length === 0 ? (
                      <div className="text-center py-12">
                        <FileAudio className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">No calls yet</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                          Upload a recording or record directly
                        </p>
                        <Button variant="outline" className="mt-4" onClick={() => setIsAddCallOpen(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Your First Call
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Search and Filter Bar */}
                        <div className="flex gap-3 items-center pb-3 border-b">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              placeholder="Search by title or source..."
                              value={callSearch}
                              onChange={(e) => setCallSearch(e.target.value)}
                              className="pl-9 pr-9"
                            />
                            {callSearch && (
                              <button
                                onClick={() => setCallSearch('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant={callStatusFilter === 'all' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCallStatusFilter('all')}
                            >
                              All
                            </Button>
                            <Button
                              variant={callStatusFilter === 'pending' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCallStatusFilter('pending')}
                            >
                              Pending
                            </Button>
                            <Button
                              variant={callStatusFilter === 'analyzed' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCallStatusFilter('analyzed')}
                            >
                              Analyzed
                            </Button>
                          </div>
                        </div>

                        {/* Results count */}
                        <div className="text-sm text-gray-500">
                          Showing {filteredCalls.length} of {callsData?.calls?.length || 0} calls
                          {(callSearch || callStatusFilter !== 'all') && (
                            <button
                              onClick={() => { setCallSearch(''); setCallStatusFilter('all'); }}
                              className="ml-2 text-blue-600 hover:underline"
                            >
                              Clear filters
                            </button>
                          )}
                        </div>

                        {filteredCalls.length === 0 ? (
                          <div className="text-center py-8">
                            <Search className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-500">No calls match your search</p>
                          </div>
                        ) : filteredCalls.map((call) => (
                          <div
                            key={call.id}
                            className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors cursor-pointer"
                            onClick={() => handleCallClick(call.id)}
                          >
                            <div className="flex items-center space-x-4">
                              <div className={cn(
                                "p-2 rounded-lg",
                                call.status === 'analyzed' ? 'bg-green-100 dark:bg-green-900' :
                                call.status === 'processing' ? 'bg-blue-100 dark:bg-blue-900' :
                                call.source === 'browser' ? 'bg-purple-100 dark:bg-purple-900' : 'bg-gray-100 dark:bg-gray-800'
                              )}>
                                {call.status === 'analyzed' ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                                ) : call.status === 'processing' ? (
                                  <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                                ) : call.source === 'browser' ? (
                                  <Mic className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                ) : (
                                  <Upload className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {call.title || `Call ${call.id.slice(0, 8)}`}
                                </p>
                                <div className="flex items-center space-x-3 text-sm text-gray-500">
                                  <span className="flex items-center">
                                    <Calendar className="h-3 w-3 mr-1" />
                                    {new Date(call.call_date).toLocaleDateString()}
                                  </span>
                                  {call.duration_seconds && (
                                    <span className="flex items-center">
                                      <Clock className="h-3 w-3 mr-1" />
                                      {formatDuration(call.duration_seconds)}
                                    </span>
                                  )}
                                  {call.status === 'analyzed' && (
                                    <Badge className="bg-purple-100 text-purple-700 text-xs">
                                      <Bot className="h-3 w-3 mr-1" />
                                      AI Analyzed
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              {getStatusBadge(call.status)}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon">
                                    <Trash2 className="h-4 w-4 text-gray-400" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    className="text-red-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteCallMutation.mutate(call.id);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Call
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <ChevronRight className="h-5 w-5 text-gray-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Emails Tab Content */}
              <TabsContent value="emails" className="mt-0">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Mail className="h-5 w-5 text-blue-500" />
                          Email Intelligence
                        </CardTitle>
                        <CardDescription>
                          {emailsData?.total || 0} emails imported
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => refetchEmails()}
                          disabled={isLoadingEmails}
                        >
                          <RefreshCw className={cn("h-4 w-4", isLoadingEmails && "animate-spin")} />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => fetchEmailsMutation.mutate()}
                          disabled={!selectedBuildId || fetchEmailsMutation.isPending}
                        >
                          {fetchEmailsMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Database className="h-4 w-4 mr-2" />
                          )}
                          Fetch from Odoo
                        </Button>
                        {emailsData?.emails && emailsData.emails.filter(e => e.status === 'pending').length > 0 && (
                          <Button
                            onClick={() => analyzeEmailsMutation.mutate()}
                            disabled={analyzeEmailsMutation.isPending}
                          >
                            {analyzeEmailsMutation.isPending ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Bot className="h-4 w-4 mr-2" />
                            )}
                            Analyze All ({emailsData.emails.filter(e => e.status === 'pending').length})
                          </Button>
                        )}
                        {failedCounts && failedCounts.emails > 0 && (
                          <Button
                            variant="outline"
                            onClick={() => retryEmailsMutation.mutate()}
                            disabled={retryEmailsMutation.isPending}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            {retryEmailsMutation.isPending ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-2" />
                            )}
                            Retry Failed ({failedCounts.emails})
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingEmails ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                      </div>
                    ) : !emailsData?.emails || emailsData.emails.length === 0 ? (
                      <div className="text-center py-12">
                        <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 font-medium">No emails imported yet</p>
                        <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
                          Click "Fetch from Odoo" to import emails from your CRM.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Search and Filter Bar */}
                        <div className="flex gap-3 items-center pb-3 border-b">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              placeholder="Search by subject, content, or sender..."
                              value={emailSearch}
                              onChange={(e) => setEmailSearch(e.target.value)}
                              className="pl-9 pr-9"
                            />
                            {emailSearch && (
                              <button
                                onClick={() => setEmailSearch('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant={emailStatusFilter === 'all' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setEmailStatusFilter('all')}
                            >
                              All
                            </Button>
                            <Button
                              variant={emailStatusFilter === 'pending' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setEmailStatusFilter('pending')}
                            >
                              Pending
                            </Button>
                            <Button
                              variant={emailStatusFilter === 'analyzed' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setEmailStatusFilter('analyzed')}
                            >
                              Analyzed
                            </Button>
                          </div>
                        </div>

                        {/* Results count */}
                        <div className="text-sm text-gray-500">
                          Showing {filteredEmails.length} of {emailsData.emails.length} emails
                          {(emailSearch || emailStatusFilter !== 'all') && (
                            <button
                              onClick={() => { setEmailSearch(''); setEmailStatusFilter('all'); }}
                              className="ml-2 text-blue-600 hover:underline"
                            >
                              Clear filters
                            </button>
                          )}
                        </div>

                        {filteredEmails.length === 0 ? (
                          <div className="text-center py-8">
                            <Search className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-500">No emails match your search</p>
                          </div>
                        ) : filteredEmails.map((email) => (
                          <div
                            key={email.id}
                            onClick={() => handleEmailClick(email.id)}
                            className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                              <div className={cn(
                                "p-2 rounded-lg",
                                email.status === 'analyzed' && "bg-green-100 dark:bg-green-900",
                                email.status === 'pending' && "bg-gray-100 dark:bg-gray-800",
                                email.status === 'processing' && "bg-blue-100 dark:bg-blue-900",
                                email.status === 'failed' && "bg-red-100 dark:bg-red-900"
                              )}>
                                {email.status === 'analyzed' ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                                ) : email.status === 'processing' ? (
                                  <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                                ) : email.status === 'failed' ? (
                                  <AlertCircle className="h-5 w-5 text-red-600" />
                                ) : (
                                  <Mail className="h-5 w-5 text-gray-600" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {email.subject || '(No subject)'}
                                </p>
                                <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                                  <span>{email.author_name || 'Unknown'}</span>
                                  <span className="text-gray-300">|</span>
                                  <span className="flex items-center">
                                    <Calendar className="h-3 w-3 mr-1" />
                                    {new Date(email.message_date).toLocaleDateString()}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {email.direction}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {email.status === 'analyzed' && (
                                <Badge className="bg-purple-100 text-purple-700">
                                  <Bot className="h-3 w-3 mr-1" />
                                  AI Analyzed
                                </Badge>
                              )}
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                              <Badge className={cn(
                                'font-medium',
                                email.status === 'analyzed' && 'bg-green-100 text-green-700',
                                email.status === 'pending' && 'bg-gray-100 text-gray-700',
                                email.status === 'processing' && 'bg-blue-100 text-blue-700',
                                email.status === 'failed' && 'bg-red-100 text-red-700'
                              )}>
                                {email.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Chatter Tab Content */}
              <TabsContent value="chatter" className="mt-0">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <MessageSquare className="h-5 w-5 text-purple-500" />
                          Chatter Intelligence
                        </CardTitle>
                        <CardDescription>
                          {chatterData?.total || 0} notes imported
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => refetchChatter()}
                          disabled={isLoadingChatter}
                        >
                          <RefreshCw className={cn("h-4 w-4", isLoadingChatter && "animate-spin")} />
                        </Button>
                        {chatterData?.chatter && chatterData.chatter.filter(c => c.status === 'pending').length > 0 && (
                          <Button
                            variant="outline"
                            onClick={() => analyzeChatterMutation.mutate()}
                            disabled={analyzeChatterMutation.isPending}
                          >
                            {analyzeChatterMutation.isPending ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Bot className="h-4 w-4 mr-2" />
                            )}
                            Analyze All ({chatterData.chatter.filter(c => c.status === 'pending').length})
                          </Button>
                        )}
                        {failedCounts && failedCounts.chatter > 0 && (
                          <Button
                            variant="outline"
                            onClick={() => retryChatterMutation.mutate()}
                            disabled={retryChatterMutation.isPending}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            {retryChatterMutation.isPending ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-2" />
                            )}
                            Retry Failed ({failedCounts.chatter})
                          </Button>
                        )}
                        <Button
                          onClick={() => fetchChatterMutation.mutate()}
                          disabled={!selectedBuildId || fetchChatterMutation.isPending}
                        >
                          {fetchChatterMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Database className="h-4 w-4 mr-2" />
                          )}
                          Fetch from Odoo
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingChatter ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                      </div>
                    ) : !chatterData?.chatter || chatterData.chatter.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 font-medium">No chatter notes imported yet</p>
                        <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
                          Click "Fetch from Odoo" to import internal notes and comments.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Search and Filter Bar */}
                        <div className="flex gap-3 items-center pb-3 border-b">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              placeholder="Search by content, author, or model..."
                              value={chatterSearch}
                              onChange={(e) => setChatterSearch(e.target.value)}
                              className="pl-9 pr-9"
                            />
                            {chatterSearch && (
                              <button
                                onClick={() => setChatterSearch('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant={chatterStatusFilter === 'all' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setChatterStatusFilter('all')}
                            >
                              All
                            </Button>
                            <Button
                              variant={chatterStatusFilter === 'pending' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setChatterStatusFilter('pending')}
                            >
                              Pending
                            </Button>
                            <Button
                              variant={chatterStatusFilter === 'analyzed' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setChatterStatusFilter('analyzed')}
                            >
                              Analyzed
                            </Button>
                          </div>
                        </div>

                        {/* Results count */}
                        <div className="text-sm text-gray-500">
                          Showing {filteredChatter.length} of {chatterData.chatter.length} messages
                          {(chatterSearch || chatterStatusFilter !== 'all') && (
                            <button
                              onClick={() => { setChatterSearch(''); setChatterStatusFilter('all'); }}
                              className="ml-2 text-blue-600 hover:underline"
                            >
                              Clear filters
                            </button>
                          )}
                        </div>

                        {filteredChatter.length === 0 ? (
                          <div className="text-center py-8">
                            <Search className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-500">No messages match your search</p>
                          </div>
                        ) : filteredChatter.map((note) => (
                          <div
                            key={note.id}
                            onClick={() => handleChatterClick(note.id)}
                            className="flex items-start gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors cursor-pointer"
                          >
                            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900 flex-shrink-0">
                              {note.status === 'analyzed' ? (
                                <CheckCircle2 className="h-5 w-5 text-green-600" />
                              ) : note.status === 'processing' ? (
                                <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                              ) : (
                                <MessageSquare className="h-5 w-5 text-purple-600" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {note.author_name || 'Unknown'}
                                </span>
                                <span className="text-gray-300">|</span>
                                <span className="flex items-center">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  {new Date(note.message_date).toLocaleDateString()}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {note.odoo_model}
                                </Badge>
                                {note.status === 'analyzed' && (
                                  <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                    <Bot className="h-3 w-3 mr-1" />
                                    AI Analyzed
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">
                                {note.body_text || '(No content)'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                              <Badge className={cn(
                                'font-medium',
                                note.status === 'analyzed' && 'bg-green-100 text-green-700',
                                note.status === 'pending' && 'bg-gray-100 text-gray-700',
                                note.status === 'processing' && 'bg-blue-100 text-blue-700',
                                note.status === 'failed' && 'bg-red-100 text-red-700'
                              )}>
                                {note.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </div>

            {/* Activity Log Sidebar */}
            <div className="lg:col-span-1">
              <Card className="h-fit">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Processing Activity
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Real-time processing status
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <div className="p-4 space-y-3">
                      {activityLog.length === 0 ? (
                        <div className="text-center py-8">
                          <Eye className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-xs text-gray-400">
                            Activity will appear here
                          </p>
                        </div>
                      ) : (
                        activityLog.map((entry) => (
                          <div key={entry.id} className="flex gap-3">
                            <div className="mt-0.5">
                              {getLogIcon(entry.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {entry.message}
                              </p>
                              {entry.details && (
                                <p className="text-xs text-gray-500 truncate">
                                  {entry.details}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {entry.timestamp.toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* AI Models Info */}
              <Card className="mt-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Bot className="h-4 w-4" />
                    AI Models
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="font-medium">Whisper</span>
                    <span className="text-gray-400">@cf/openai/whisper</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="font-medium">LLaMA 3.3 70B</span>
                    <span className="text-gray-400">Analysis</span>
                  </div>
                  <Separator />
                  <div className="text-xs text-gray-500">
                    <p>Storage: <span className="font-mono">R2 doploy-insights-audio</span></p>
                    <p>Database: <span className="font-mono">D1 odoo-agent-db</span></p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </Tabs>
      </div>

      {/* Email Detail Dialog */}
      <Dialog open={isEmailDetailOpen} onOpenChange={setIsEmailDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {isLoadingEmailDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : selectedEmail ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {selectedEmail.subject || '(No subject)'}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-3">
                  <span>{selectedEmail.author_name || 'Unknown'}</span>
                  <span className="text-gray-300">|</span>
                  <span>{new Date(selectedEmail.message_date).toLocaleString()}</span>
                  <Badge variant="outline">{selectedEmail.direction}</Badge>
                </DialogDescription>
              </DialogHeader>

              {/* Email Body */}
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Content</h4>
                <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm text-gray-600 dark:text-gray-400 max-h-40 overflow-y-auto whitespace-pre-wrap">
                  {selectedEmail.body_text || '(No content)'}
                </div>
              </div>

              {/* AI Analysis */}
              {selectedEmail.analysis ? (
                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">AI Analysis</h3>
                  </div>

                  {/* Summary */}
                  {selectedEmail.analysis.summary && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Summary</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{selectedEmail.analysis.summary}</p>
                    </div>
                  )}

                  {/* Sentiment & Urgency */}
                  <div className="flex gap-4">
                    {selectedEmail.analysis.sentiment && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sentiment</h4>
                        <Badge className={cn(
                          selectedEmail.analysis.sentiment === 'positive' && 'bg-green-100 text-green-700',
                          selectedEmail.analysis.sentiment === 'negative' && 'bg-red-100 text-red-700',
                          selectedEmail.analysis.sentiment === 'neutral' && 'bg-gray-100 text-gray-700',
                          selectedEmail.analysis.sentiment === 'mixed' && 'bg-yellow-100 text-yellow-700'
                        )}>
                          {selectedEmail.analysis.sentiment}
                          {selectedEmail.analysis.sentiment_score !== null && (
                            <span className="ml-1 opacity-75">({Math.round(selectedEmail.analysis.sentiment_score * 100)}%)</span>
                          )}
                        </Badge>
                      </div>
                    )}
                    {selectedEmail.analysis.urgency_level && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Urgency</h4>
                        <Badge className={cn(
                          selectedEmail.analysis.urgency_level === 'low' && 'bg-gray-100 text-gray-700',
                          selectedEmail.analysis.urgency_level === 'medium' && 'bg-yellow-100 text-yellow-700',
                          selectedEmail.analysis.urgency_level === 'high' && 'bg-orange-100 text-orange-700',
                          selectedEmail.analysis.urgency_level === 'critical' && 'bg-red-100 text-red-700'
                        )}>
                          {selectedEmail.analysis.urgency_level}
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Topics */}
                  {selectedEmail.analysis.topics && selectedEmail.analysis.topics.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Topics</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedEmail.analysis.topics.map((topic, i) => (
                          <Badge key={i} variant="outline">{topic}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Points */}
                  {selectedEmail.analysis.key_points && selectedEmail.analysis.key_points.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Key Points</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {selectedEmail.analysis.key_points.map((point, i) => (
                          <li key={i}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Items */}
                  {selectedEmail.analysis.action_items && selectedEmail.analysis.action_items.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Action Items</h4>
                      <div className="space-y-2">
                        {selectedEmail.analysis.action_items.map((item, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                            <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm text-gray-700 dark:text-gray-300">{item.description}</p>
                              {(item.assignee || item.priority) && (
                                <div className="flex gap-2 mt-1">
                                  {item.assignee && <Badge variant="outline" className="text-xs">{item.assignee}</Badge>}
                                  {item.priority && (
                                    <Badge className={cn(
                                      'text-xs',
                                      item.priority === 'high' && 'bg-red-100 text-red-700',
                                      item.priority === 'medium' && 'bg-yellow-100 text-yellow-700',
                                      item.priority === 'low' && 'bg-gray-100 text-gray-700'
                                    )}>
                                      {item.priority}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Suggested Response */}
                  {selectedEmail.analysis.response_suggested && selectedEmail.analysis.suggested_response && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Suggested Response</h4>
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-sm text-gray-700 dark:text-gray-300 italic">
                        {selectedEmail.analysis.suggested_response}
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedEmail.status === 'failed' ? (
                <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                      <AlertCircle className="h-5 w-5" />
                      <h4 className="font-medium">Analysis Failed</h4>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => retrySingleEmailMutation.mutate(selectedEmail.id)}
                      disabled={retrySingleEmailMutation.isPending}
                      className="text-red-600 border-red-200 hover:bg-red-100"
                    >
                      {retrySingleEmailMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-1" />
                      )}
                      Retry
                    </Button>
                  </div>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {selectedEmail.error_message || 'An unknown error occurred during analysis.'}
                  </p>
                </div>
              ) : (
                <div className="mt-6 text-center py-8 text-gray-500">
                  <Bot className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p className="font-medium">No AI analysis available</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Click "Analyze All" to run AI analysis on this email
                  </p>
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Chatter Detail Dialog */}
      <Dialog open={isChatterDetailOpen} onOpenChange={setIsChatterDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {isLoadingChatterDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : selectedChatter ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-purple-600" />
                  Chatter Message
                </DialogTitle>
                <DialogDescription className="flex items-center gap-3">
                  <span>{selectedChatter.author_name || 'Unknown'}</span>
                  <span className="text-gray-300">|</span>
                  <span>{new Date(selectedChatter.message_date).toLocaleString()}</span>
                  <Badge variant="outline">{selectedChatter.odoo_model}</Badge>
                </DialogDescription>
              </DialogHeader>

              {/* Message Body */}
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Content</h4>
                <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm text-gray-600 dark:text-gray-400 max-h-40 overflow-y-auto whitespace-pre-wrap">
                  {selectedChatter.body_text || '(No content)'}
                </div>
              </div>

              {/* AI Analysis */}
              {selectedChatter.analysis ? (
                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">AI Analysis</h3>
                  </div>

                  {/* Summary */}
                  {selectedChatter.analysis.summary && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Summary</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{selectedChatter.analysis.summary}</p>
                    </div>
                  )}

                  {/* Sentiment & Urgency */}
                  <div className="flex gap-4">
                    {selectedChatter.analysis.sentiment && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sentiment</h4>
                        <Badge className={cn(
                          selectedChatter.analysis.sentiment === 'positive' && 'bg-green-100 text-green-700',
                          selectedChatter.analysis.sentiment === 'negative' && 'bg-red-100 text-red-700',
                          selectedChatter.analysis.sentiment === 'neutral' && 'bg-gray-100 text-gray-700',
                          selectedChatter.analysis.sentiment === 'mixed' && 'bg-yellow-100 text-yellow-700'
                        )}>
                          {selectedChatter.analysis.sentiment}
                          {selectedChatter.analysis.sentiment_score !== null && (
                            <span className="ml-1 opacity-75">({Math.round(selectedChatter.analysis.sentiment_score * 100)}%)</span>
                          )}
                        </Badge>
                      </div>
                    )}
                    {selectedChatter.analysis.urgency_level && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Urgency</h4>
                        <Badge className={cn(
                          selectedChatter.analysis.urgency_level === 'low' && 'bg-gray-100 text-gray-700',
                          selectedChatter.analysis.urgency_level === 'medium' && 'bg-yellow-100 text-yellow-700',
                          selectedChatter.analysis.urgency_level === 'high' && 'bg-orange-100 text-orange-700',
                          selectedChatter.analysis.urgency_level === 'critical' && 'bg-red-100 text-red-700'
                        )}>
                          {selectedChatter.analysis.urgency_level}
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Topics */}
                  {selectedChatter.analysis.topics && selectedChatter.analysis.topics.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Topics</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedChatter.analysis.topics.map((topic, i) => (
                          <Badge key={i} variant="outline">{topic}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Points */}
                  {selectedChatter.analysis.key_points && selectedChatter.analysis.key_points.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Key Points</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {selectedChatter.analysis.key_points.map((point, i) => (
                          <li key={i}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Items */}
                  {selectedChatter.analysis.action_items && selectedChatter.analysis.action_items.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Action Items</h4>
                      <div className="space-y-2">
                        {selectedChatter.analysis.action_items.map((item, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                            <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm text-gray-700 dark:text-gray-300">{item.description}</p>
                              {(item.assignee || item.priority) && (
                                <div className="flex gap-2 mt-1">
                                  {item.assignee && <Badge variant="outline" className="text-xs">{item.assignee}</Badge>}
                                  {item.priority && (
                                    <Badge className={cn(
                                      'text-xs',
                                      item.priority === 'high' && 'bg-red-100 text-red-700',
                                      item.priority === 'medium' && 'bg-yellow-100 text-yellow-700',
                                      item.priority === 'low' && 'bg-gray-100 text-gray-700'
                                    )}>
                                      {item.priority}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Suggested Follow-up */}
                  {selectedChatter.analysis.response_suggested && selectedChatter.analysis.suggested_response && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Suggested Follow-up</h4>
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-sm text-gray-700 dark:text-gray-300 italic">
                        {selectedChatter.analysis.suggested_response}
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedChatter.status === 'failed' ? (
                <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                      <AlertCircle className="h-5 w-5" />
                      <h4 className="font-medium">Analysis Failed</h4>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => retrySingleChatterMutation.mutate(selectedChatter.id)}
                      disabled={retrySingleChatterMutation.isPending}
                      className="text-red-600 border-red-200 hover:bg-red-100"
                    >
                      {retrySingleChatterMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-1" />
                      )}
                      Retry
                    </Button>
                  </div>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {selectedChatter.error_message || 'An unknown error occurred during analysis.'}
                  </p>
                </div>
              ) : (
                <div className="mt-6 text-center py-8 text-gray-500">
                  <Bot className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p className="font-medium">No AI analysis available</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Click "Analyze All" to run AI analysis on this message
                  </p>
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Call Detail Dialog */}
      <Dialog open={isCallDetailOpen} onOpenChange={setIsCallDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {isLoadingCallDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : selectedCall ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-blue-600" />
                  {selectedCall.title || `Call ${selectedCall.id.slice(0, 8)}`}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-3">
                  <span className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(selectedCall.call_date).toLocaleString()}
                  </span>
                  {selectedCall.duration_seconds && (
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {formatDuration(selectedCall.duration_seconds)}
                    </span>
                  )}
                  <Badge variant="outline">{selectedCall.source}</Badge>
                  <Badge className={cn(
                    selectedCall.status === 'analyzed' && 'bg-green-100 text-green-700',
                    selectedCall.status === 'transcribed' && 'bg-yellow-100 text-yellow-700',
                    selectedCall.status === 'processing' && 'bg-blue-100 text-blue-700',
                    selectedCall.status === 'pending' && 'bg-gray-100 text-gray-700',
                    selectedCall.status === 'failed' && 'bg-red-100 text-red-700'
                  )}>
                    {selectedCall.status}
                  </Badge>
                </DialogDescription>
              </DialogHeader>

              {/* Transcript */}
              {selectedCall.transcript && selectedCall.transcript.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <FileAudio className="h-4 w-4" />
                    Transcript ({selectedCall.transcript.length} segments)
                  </h4>
                  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg max-h-60 overflow-y-auto space-y-2">
                    {selectedCall.transcript.map((segment, i) => (
                      <div key={segment.id || i} className="text-sm">
                        {segment.speaker_name && (
                          <span className="font-medium text-blue-600 mr-2">{segment.speaker_name}:</span>
                        )}
                        <span className="text-gray-600 dark:text-gray-400">{segment.text}</span>
                        {segment.start_time_ms !== null && (
                          <span className="text-xs text-gray-400 ml-2">
                            [{Math.floor(segment.start_time_ms / 1000)}s]
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Analysis */}
              {selectedCall.analysis ? (
                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">AI Analysis</h3>
                  </div>

                  {/* Summary */}
                  {selectedCall.analysis.summary && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Summary</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{selectedCall.analysis.summary}</p>
                    </div>
                  )}

                  {/* Sentiment & Talk Ratio */}
                  <div className="flex gap-4 flex-wrap">
                    {selectedCall.analysis.sentiment && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sentiment</h4>
                        <Badge className={cn(
                          selectedCall.analysis.sentiment === 'positive' && 'bg-green-100 text-green-700',
                          selectedCall.analysis.sentiment === 'negative' && 'bg-red-100 text-red-700',
                          selectedCall.analysis.sentiment === 'neutral' && 'bg-gray-100 text-gray-700',
                          selectedCall.analysis.sentiment === 'mixed' && 'bg-yellow-100 text-yellow-700'
                        )}>
                          {selectedCall.analysis.sentiment}
                          {selectedCall.analysis.sentiment_score !== null && (
                            <span className="ml-1 opacity-75">({Math.round(selectedCall.analysis.sentiment_score * 100)}%)</span>
                          )}
                        </Badge>
                      </div>
                    )}
                    {selectedCall.analysis.talk_ratio && Object.keys(selectedCall.analysis.talk_ratio).length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Talk Ratio</h4>
                        <div className="flex gap-2">
                          {Object.entries(selectedCall.analysis.talk_ratio).map(([speaker, ratio]) => (
                            <Badge key={speaker} variant="outline">
                              {speaker}: {Math.round(ratio * 100)}%
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Topics */}
                  {selectedCall.analysis.topics && selectedCall.analysis.topics.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Topics Discussed</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedCall.analysis.topics.map((topic, i) => (
                          <Badge key={i} variant="outline">{topic}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Objections */}
                  {selectedCall.analysis.objections && selectedCall.analysis.objections.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Objections Raised</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {selectedCall.analysis.objections.map((objection, i) => (
                          <li key={i}>{objection}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Competitors Mentioned */}
                  {selectedCall.analysis.competitors_mentioned && selectedCall.analysis.competitors_mentioned.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Competitors Mentioned</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedCall.analysis.competitors_mentioned.map((competitor, i) => (
                          <Badge key={i} variant="outline" className="bg-red-50 text-red-700">{competitor}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Items */}
                  {selectedCall.analysis.action_items && selectedCall.analysis.action_items.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Action Items</h4>
                      <div className="space-y-2">
                        {selectedCall.analysis.action_items.map((item, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                            <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm text-gray-700 dark:text-gray-300">{item.description}</p>
                              {(item.assignee || item.priority || item.due_date) && (
                                <div className="flex gap-2 mt-1 flex-wrap">
                                  {item.assignee && <Badge variant="outline" className="text-xs">{item.assignee}</Badge>}
                                  {item.priority && (
                                    <Badge className={cn(
                                      'text-xs',
                                      item.priority === 'high' && 'bg-red-100 text-red-700',
                                      item.priority === 'medium' && 'bg-yellow-100 text-yellow-700',
                                      item.priority === 'low' && 'bg-gray-100 text-gray-700'
                                    )}>
                                      {item.priority}
                                    </Badge>
                                  )}
                                  {item.due_date && <Badge variant="outline" className="text-xs">{item.due_date}</Badge>}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Quotes */}
                  {selectedCall.analysis.key_quotes && selectedCall.analysis.key_quotes.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Key Quotes</h4>
                      <div className="space-y-2">
                        {selectedCall.analysis.key_quotes.map((quote, i) => (
                          <div key={i} className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded italic text-sm text-gray-700 dark:text-gray-300">
                            "{quote.text}"
                            {quote.speaker && <span className="text-purple-600 not-italic ml-2">- {quote.speaker}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Next Steps */}
                  {selectedCall.analysis.next_steps && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Next Steps</h4>
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                        {selectedCall.analysis.next_steps}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-6 text-center py-8 text-gray-500">
                  <Bot className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p className="font-medium">No AI analysis available</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {selectedCall.status === 'pending' && 'Call is waiting to be processed'}
                    {selectedCall.status === 'processing' && 'Call is currently being processed'}
                    {selectedCall.status === 'transcribed' && 'Transcript available, analysis pending'}
                    {selectedCall.status === 'failed' && 'Processing failed - check error message'}
                  </p>
                </div>
              )}

              {/* Scorecards */}
              {selectedCall.scorecards && selectedCall.scorecards.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Coaching Scorecards</h4>
                  {selectedCall.scorecards.map((scorecard, i) => (
                    <div key={scorecard.id || i} className="p-3 border rounded-lg mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline">{scorecard.framework.toUpperCase()}</Badge>
                        {scorecard.overall_score !== null && (
                          <span className={cn(
                            "font-bold text-lg",
                            scorecard.overall_score >= 4 && "text-green-600",
                            scorecard.overall_score >= 2.5 && scorecard.overall_score < 4 && "text-yellow-600",
                            scorecard.overall_score < 2.5 && "text-red-600"
                          )}>
                            {scorecard.overall_score.toFixed(1)}/5
                          </span>
                        )}
                      </div>
                      {scorecard.feedback && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">{scorecard.feedback}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Sync Settings Modal */}
      {selectedBuildId && (
        <SyncSettingsModal
          open={isSyncSettingsOpen}
          onOpenChange={setIsSyncSettingsOpen}
          projectId={projectId!}
          buildId={selectedBuildId}
        />
      )}
    </DashboardLayout>
  );
}
